import { Injectable } from '@angular/core';
import { SeemoreUiViewContext } from './seemore-ui-view-context.model';

/**
 * Root bridge that lets the global seemore chat widget read the *currently active*
 * grid's view context at send time.
 *
 * The chat request is assembled in a root-scoped singleton, but grid view-state
 * lives in a module-scoped `EntityListStateService` (one instance per grid). A grid
 * page registers a live provider on init and clears it on destroy; the ai-chat
 * request assembler calls `getActiveViewContext()` at send time.
 *
 * Registering a *provider function* (not a snapshot copy) keeps the value fresh at
 * send time and `null` whenever no grid is mounted.
 *
 * Assumes at most one `<entity-list>` is mounted at a time (true across the app
 * today), so a single provider slot is enough. If a page ever mounts two grids
 * concurrently (split view, a dialog grid over a base grid), only the last-registered
 * one is reported and this must become a stack of providers.
 */
@Injectable({ providedIn: 'root' })
export class SeemoreUiViewContextService {
    private provider: (() => SeemoreUiViewContext) | null = null;

    registerActiveView(provider: () => SeemoreUiViewContext): void {
        this.provider = provider;
    }

    /**
     * Deregisters `provider`, but only if it is still the active one — so a grid
     * destroyed *after* the next grid has already registered does not wipe the new
     * grid's context (navigation race).
     */
    clearActiveView(provider: () => SeemoreUiViewContext): void {
        if (this.provider === provider) {
            this.provider = null;
        }
    }

    getActiveViewContext(): SeemoreUiViewContext {
        return this.provider?.() ?? null;
    }
}
